import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { releaseApi } from "./services/release.service";
import { documentApi } from "./services/document.service";
import { productSheetApi } from "./services/product-sheet.service";
import { webinarApi, getThumbnailUrl } from "./services/webinar.service";
import { graphicsAPI } from "./pages/graphics/api";
import { brandGuideAPI } from "./pages/brand-guide/api";
import FileTypeThumbnail from "./components/FileTypeThumbnail";
import { courseApi, getThumbnailUrl as getCourseThumb } from "./services/course.service";
import { useWhatsNewContext } from "./hooks/WhatsNewContext";
import { useT, useI18n } from "./i18n";

// Raw backend values only; labels, fallbacks and dates are applied at render
// time so switching language doesn't refetch.
type CardData = {
  key: string;
  i18nKey: string; // dashboard.cards.<i18nKey>.{tag,title,body,cta}
  badgeIcon: string;
  iconColor: string; // tailwind text color for the fallback icon
  image?: string | null; // banner image (video thumbnail / presigned preview)
  isVideo?: boolean; // show a play glyph over the image
  fileType?: string; // when no image: render a file-type tile (PDF/ZIP/…)
  fileKey?: string; // used to derive the extension if fileType is missing
  isNew?: boolean; // created since the user's last dashboard visit
  title?: string;
  fallbackTitle?: boolean; // use dashboard.cards.<i18nKey>.title when title is missing
  version?: string;
  description?: string;
  createdAt?: string;
  onClick: () => void;
};

const formatDate = (value: string | undefined, locale: string) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
};

const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp", ".bmp"];
const isImageKey = (key?: string) =>
  !!key && IMAGE_EXTS.some((ext) => key.toLowerCase().includes(ext));

const CardSkeleton: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden animate-pulse">
    <div className="w-full h-40 bg-gray-200" />
    <div className="p-6 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-5/6" />
    </div>
  </div>
);

const NewsCard: React.FC<{ card: CardData }> = ({ card }) => {
  const { t, code } = useI18n();
  const k = `dashboard.cards.${card.i18nKey}`;
  const title = `${card.title ?? (card.fallbackTitle ? t(`${k}.title`) : "")}${
    card.version ? ` ${card.version}` : ""
  }`;
  const date = formatDate(card.createdAt, code);
  return (
  <div
    onClick={card.onClick}
    className="group bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col"
  >
    {/* Banner */}
    <div className="relative w-full h-40 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center overflow-hidden">
      {card.image ? (
        <img
          src={card.image}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : card.fileType || card.fileKey ? (
        <FileTypeThumbnail
          fileType={card.fileType}
          fileKey={card.fileKey}
          fallbackIcon={card.badgeIcon}
        />
      ) : (
        <i className={`${card.badgeIcon} text-4xl text-white/30`}></i>
      )}
      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"></div>
      {card.isVideo && card.image && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="h-12 w-12 rounded-full bg-white/85 flex items-center justify-center shadow group-hover:scale-105 transition-transform">
            <i className="fa-solid fa-play text-gray-800 ml-0.5"></i>
          </span>
        </span>
      )}
      <span className="absolute bottom-4 left-4 text-white text-lg font-medium drop-shadow">
        {t(`${k}.tag`)}
      </span>
      {card.isNew && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-white shadow">
          <span className="h-1.5 w-1.5 rounded-full bg-white"></span>
          {t("common.new")}
        </span>
      )}
    </div>
    {/* Body */}
    <div className="p-6 flex flex-col flex-1">
      {date && (
        <div className="flex items-center gap-2 mb-3">
          <i className="fa-solid fa-calendar text-gray-500 text-sm"></i>
          <span className="text-sm text-gray-500">{date}</span>
        </div>
      )}
      <h3 className="text-lg font-medium text-gray-900 mb-3 line-clamp-2">
        {title}
      </h3>
      <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-3 flex-1">
        {card.description || t(`${k}.body`)}
      </p>
      <span className="text-gray-700 group-hover:text-gray-900 font-medium flex items-center gap-2 text-sm">
        {t(`${k}.cta`)} <i className="fa-solid fa-arrow-right text-xs"></i>
      </span>
    </div>
  </div>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useT();

  const [cards, setCards] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(true);

  // Shared "What's New" feed (also drives the sidebar bell + per-section dots).
  // `newKeys` is the set of dashboard card keys with unseen new content.
  const { newCount, newKeys } = useWhatsNewContext();

  const companyName = user?.companyName || user?.username || t("dashboard.partnerFallback");

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);

      // Fetch every source independently so one failure can't blank the rest.
      const [
        releaseRes,
        webinarRes,
        documentRes,
        sheetRes,
        graphicsRes,
        brandRes,
        courseRes,
      ] = await Promise.allSettled([
        releaseApi.getAllReleases(),
        webinarApi.getAllWebinars(),
        documentApi.getAllDocuments(),
        productSheetApi.getAllProductSheets(),
        graphicsAPI.getAllItems(),
        brandGuideAPI.getAllItems(),
        courseApi.getAllCourses(),
      ]);

      const built: CardData[] = [];

      if (releaseRes.status === "fulfilled") {
        const r = releaseRes.value.data?.[0];
        if (r)
          built.push({
            key: "release",
            i18nKey: "release",
            badgeIcon: "fa-solid fa-rocket",
            iconColor: "text-blue-300",
            fileType: r.fileType,
            fileKey: r.downloadKey,
            title: r.releaseName,
            fallbackTitle: true,
            version: r.version,
            description: r.description,
            createdAt: r.createdAt,
            onClick: () => navigate("/release"),
          });
      }

      if (webinarRes.status === "fulfilled") {
        const w = webinarRes.value.data?.[0];
        if (w)
          built.push({
            key: "webinar",
            i18nKey: "webinar",
            badgeIcon: "fa-solid fa-video",
            iconColor: "text-red-300",
            image: getThumbnailUrl(w.videoUrl, w.source),
            isVideo: true,
            title: w.title,
            description: w.description,
            createdAt: w.createdAt,
            onClick: () => navigate(`/webinar/${w.id}`),
          });
      }

      if (documentRes.status === "fulfilled") {
        const d = documentRes.value.data?.[0];
        if (d)
          built.push({
            key: "document",
            i18nKey: "document",
            badgeIcon: "fa-solid fa-file-lines",
            iconColor: "text-purple-300",
            title: d.title,
            description: d.description,
            createdAt: d.createdAt,
            onClick: () => navigate("/documents"),
          });
      }

      if (sheetRes.status === "fulfilled") {
        const s = sheetRes.value.data?.[0];
        if (s)
          built.push({
            key: "sheet",
            i18nKey: "productSheet",
            badgeIcon: "fa-solid fa-file-invoice",
            iconColor: "text-emerald-300",
            fileType: s.fileType,
            fileKey: s.downloadKey,
            title: s.sheetName,
            fallbackTitle: true,
            version: s.version,
            description: s.description,
            createdAt: s.createdAt,
            onClick: () => navigate("/product-sheet"),
          });
      }

      if (graphicsRes.status === "fulfilled") {
        const g = graphicsRes.value?.[0];
        if (g)
          built.push({
            key: "graphics",
            i18nKey: "graphics",
            badgeIcon: "fa-solid fa-image",
            iconColor: "text-pink-300",
            fileKey: g.fileKey,
            title: g.title,
            description: g.description,
            createdAt: g.createdAt,
            onClick: () => navigate("/graphics"),
          });
      }

      if (brandRes.status === "fulfilled") {
        const b = brandRes.value?.[0];
        if (b)
          built.push({
            key: "brand",
            i18nKey: "brandGuide",
            badgeIcon: "fa-solid fa-palette",
            iconColor: "text-amber-300",
            fileKey: b.fileKey,
            title: b.title,
            description: b.description,
            createdAt: b.createdAt,
            onClick: () => navigate("/brand-guide"),
          });
      }

      if (courseRes.status === "fulfilled") {
        const c = courseRes.value.data?.[0];
        if (c) {
          // Prefer a pasted public thumbnail; else the first video lesson's
          // thumbnail; else fall back to the academy glyph.
          const firstVideo = (c.sections || [])
            .flatMap((sec) => sec.lessons || [])
            .find((l) => l.type === "video" && l.videoUrl);
          const image =
            c.thumbnailUrl ||
            (firstVideo
              ? getCourseThumb(firstVideo.videoUrl, firstVideo.source)
              : null);
          built.push({
            key: "academy",
            i18nKey: "academy",
            badgeIcon: "fa-solid fa-graduation-cap",
            iconColor: "text-indigo-300",
            image,
            isVideo: !!image,
            title: c.title,
            description: c.description,
            createdAt: c.createdAt,
            onClick: () => navigate(`/academy/${c.id}`),
          });
        }
      }

      if (active) {
        setCards(built);
        setLoading(false);
      }

      // Resolve presigned image previews for graphics / brand-guide cards that
      // point at an image file. Done after the initial render so cards show fast.
      const previewJobs: Promise<void>[] = [];

      if (graphicsRes.status === "fulfilled") {
        const g = graphicsRes.value?.[0];
        if (g && isImageKey(g.fileKey)) {
          previewJobs.push(
            graphicsAPI
              .getPreviewUrl(g.id)
              .then((url) => {
                if (!active) return;
                setCards((prev) =>
                  prev.map((c) => (c.key === "graphics" ? { ...c, image: url } : c))
                );
              })
              .catch(() => {})
          );
        }
      }

      if (brandRes.status === "fulfilled") {
        const b = brandRes.value?.[0];
        if (b && isImageKey(b.fileKey)) {
          previewJobs.push(
            brandGuideAPI
              .getPreviewUrl(b.id)
              .then((url) => {
                if (!active) return;
                setCards((prev) =>
                  prev.map((c) => (c.key === "brand" ? { ...c, image: url } : c))
                );
              })
              .catch(() => {})
          );
        }
      }

      await Promise.allSettled(previewJobs);
    };

    load();
    return () => {
      active = false;
    };
  }, [navigate]);

  return (
    <main id="main-content" className="flex-1 p-8 overflow-y-auto">
      <div id="main-header" className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            {t("dashboard.welcome", { name: companyName })}
          </h1>
          <p className="text-gray-600">
            {t("dashboard.subtitle")}
          </p>
        </div>
        {newCount > 0 && (
          <div className="flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
            </span>
            {t(
              newCount === 1
                ? "dashboard.newUpdatesOne"
                : "dashboard.newUpdatesOther",
              { count: newCount }
            )}
          </div>
        )}
      </div>

      <section id="news-section">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          {t("dashboard.latestNews")}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-lg p-12 text-center">
            <i className="fa-regular fa-newspaper text-3xl text-gray-300"></i>
            <p className="mt-3 text-sm text-gray-500">
              {t("dashboard.empty")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <NewsCard
                key={card.key}
                card={{ ...card, isNew: newKeys.has(card.key) }}
              />
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Dashboard;
